export function flattenObject(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  function flatten(nested: Record<string, unknown>, prevKey = '') {
    for (const [key, value] of Object.entries(nested)) {
      const newKey = prevKey ? `${prevKey}.${key}` : key
      if (isArrayOfObjects(value))
        value.forEach((val, index: number) => flatten(val, `${newKey}[${index}]`))
      else if (isRecord(value))
        flatten(value, newKey)
      else
        result[newKey] = value
    }
  }

  flatten(obj)

  return result
}

function isObject(value: unknown): value is Record<string, unknown> | unknown[] {
  return typeof value === 'object' && value !== null
}

export function isArray<T>(obj: unknown): obj is T[] {
  return Array.isArray(obj)
}

export function isNullOrUndefined(value: unknown): value is null | undefined {
  return value === null || value === undefined
}

export function isNumber<T>(value: T): boolean {
  if (isNullOrUndefined(value) || typeof value === 'boolean' || String(value).trim() === '')
    return false

  return !Number.isNaN(toNumber(value))
}

export function toNumber<T>(value: T): number {
  const val = value && value.toString().replace(/\s/g, '').replace(',', '.')

  return Number(val)
}

function isArrayOfObjects(value: unknown): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.every(val => isRecord(val))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return isObject(value) && !Array.isArray(value)
}

export function splitName(name: string): [string, string] {
  const firstSpaceIndex = (name || '').indexOf(' ')
  if (firstSpaceIndex >= 0) {
    const firstPart = name.substring(0, firstSpaceIndex)
    const secondPart = name.substring(firstSpaceIndex + 1)

    return [firstPart, secondPart]
  }

  return [name || '', '']
}

export function createRange(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, index) => start + index)
}

export function appendPrefixToKeys(record: Record<string, unknown>, prefix: string): Record<string, string> {
  return Object.keys(record || {}).reduce((newRecord, key) => ({ ...newRecord, [`${prefix}${key}`]: record[key] }), {})
}

export function mapKeys(record: Record<string, unknown>, mapping: Record<string, string>): Record<string, unknown> {
  return Object.keys(record || {}).reduce((newRecord, key) => ({
    ...newRecord,
    [mapping[key] || key]: record[key],
  }), {})
}

export function appendObjectsToEnd<T extends { id?: number | null }>(objects: T[], newObjects: T[]): T[] {
  const copy = [...objects]
  const ids = copy.map(obj => obj.id)
  for (const obj of newObjects) {
    if (!ids.includes(obj.id))
      copy.push(obj)
  }

  return copy
}

export function appendObjectsToStart<T extends { id?: number | null }>(objects: T[], newObjects: T[]): T[] {
  const copy = [...objects]
  const ids = copy.map(obj => obj.id)
  const reversed = [...newObjects].reverse()
  for (const obj of reversed) {
    if (!ids.includes(obj.id))
      copy.unshift(obj)
  }

  return copy
}

export function normalizeString(value: string | null | undefined): string {
  return removeAccents(value || '').toLowerCase()
}

export function highlightString(valueWithoutAccent: string, originalValue: string, term: string): string | undefined {
  const index = valueWithoutAccent.indexOf(term)
  if (index >= 0)
    return `${originalValue.substring(0, index)}<b>${originalValue.substring(index, index + term.length)}</b>${originalValue.substring(index + term.length)}`

  return undefined
}

export function removeAccents(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036F]/g, '')
}

export function roundNoTrailingZeros(value: string | number | null | undefined, maxFractionDigits: number): string | undefined {
  return isNullOrUndefined(value) || value === '' ? undefined : String(+Number.parseFloat(String(value)).toFixed(maxFractionDigits))
}

export function uuidv4(): string {
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
    (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16),
  )
}

export function highlightValueIfChanged(oldValue: string | null | undefined, newValue: string | null | undefined, newLine: boolean = true, highlightColor: string = 'text-text-warning'): string | undefined {
  if (!oldValue && !newValue)
    return undefined
  else if (oldValue && newValue && oldValue !== newValue)
    return `<span class="${highlightColor} font-semibold">${newValue}</span> ${newLine ? '<br/>' : ''} <del class="text-text-muted text-sm">${oldValue}</del>`
  else if (!oldValue && newValue)
    return `<span class="${highlightColor} font-semibold">${newValue}</span>`
  else if (oldValue && (newValue === '' || isNullOrUndefined(newValue)))
    return `<del class="text-text-muted">${oldValue}</del>`
  else
    return oldValue || undefined
}

export function constructPairsByOriginalId<
  T extends { originalId?: number | null; id?: number | null },
>(
  items: T[],
  updatedItems?: T[],
): [T | undefined, T | undefined][] {
  if (!updatedItems) {
    updatedItems = items
  }

  // If both original and updated items are empty, return empty array
  if (items.length === 0 && updatedItems.length === 0) {
    return []
  }

  // Handle case when there are no updated items (empty array means all items were removed)
  if (updatedItems.length === 0) {
    return items.map(item => [item, undefined])
  }

  const pairs: [T | undefined, T | undefined][] = []
  const originalMap = new Map<number, { item: T; index: number }>()
  const usedUpdated = new Set<number>()
  const usedOriginalIds = new Set<number>()

  // Build a map of original items by id for fast lookup
  items.forEach((item, idx) => {
    if (item.id != null) originalMap.set(item.id, { item, index: idx })
  })

  // Try to pair updated items by originalId
  updatedItems.forEach((updated, uIdx) => {
    const origId = updated.originalId
    if (origId != null && originalMap.has(origId)) {
      const { item: originalItem, index } = originalMap.get(origId)!

      pairs[index] = [originalItem, updated]
      usedUpdated.add(uIdx)
      usedOriginalIds.add(origId)
    }
  })

  // Handle original items that were not matched
  items.forEach((original, idx) => {
    if (original.id != null && !usedOriginalIds.has(original.id)) {
      pairs[idx] = [original, undefined]
    }
  })

  // Collect updated items that weren't matched (new items)
  const orphanUpdated = updatedItems.filter((_, idx) => !usedUpdated.has(idx))

  // Fallback: if no originalId matches at all, pair by index
  if (usedUpdated.size === 0 && items.length > 0) {
    const maxLen = Math.max(items.length, updatedItems.length)
    const fullPairs: [T | undefined, T | undefined][] = []
    for (let i = 0; i < maxLen; i++) {
      fullPairs.push([
        i < items.length ? items[i] : undefined,
        i < updatedItems.length ? updatedItems[i] : undefined,
      ])
    }

    return fullPairs
  }

  // Append new items (orphans) at the end
  orphanUpdated.forEach(updated => pairs.push([undefined, updated]))

  return pairs
}

export function formatFileSize(bytes: number) {
  if (!bytes || bytes < 0) return '0 bytes'
  if (bytes === 1) return '1 byte'

  const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1000))
  const decimalPlaces = i >= 2 ? 2 : 0 // we only want to show decimal places for MB and bigger
  const fileSize = (bytes / Math.pow(1000, i)).toFixed(decimalPlaces)

  return `${fileSize} ${sizes[i]}`
}

export function isInputTextInOptions(options: Array<{ label: string; value: string | number }> | Record<string, string>, inputText: string): boolean {
  const normalizedInput = normalizeString(inputText.trim())
  let optionsArray

  if (Array.isArray(options))
    optionsArray = options
  else
    optionsArray = Object.keys(options).map(key => ({ label: options[key], value: key }))

  return optionsArray.some(
    option => normalizeString(option.label) === normalizedInput,
  )
}
